// src/utils/mergeBillingPings.js
//
// Merge duplicate SDK-bidding billing pings (validData.kind === 'bill') for the
// Raw requests tab (Slot Story step 4, requirements AC 3.22-3.27, 3.36-3.38).
//
// PRESENTATION ONLY. Nothing here removes a row from state, HAR export or Checks
// input; the table just folds the later pings under the first one ("x3") and lets
// the user expand them. Duplicate billing is a FINDING, not noise: a merged group
// with N>1 carries an info issue `duplicate_billing_ping`, and the slot story
// states N in its why[] (utils/slotStory.js, opts.duplicateBills).
//
// Merge key = deviceId + networkId + slot key + host + path + sorted non-volatile
// query params. Rules:
//  - `settlement_price` is always in the key (two prices = two settlements).
//  - The volatile set (clock / cache-busters) is per network, enumerated beside the
//    registry in proxy-server/mediationNetworks.js (`volatileParams`,
//    `billVolatileParams`). This is its twin, pinned by billingMergeParity.test.js.
//    A param that is NOT listed is included in the key, so an unknown differing
//    param keeps two pings apart (safe direction).
//  - A ping that is in no slot lifecycle, or in more than one (ambiguous mediation),
//    is never merged.
//  - Truncated URLs (`ingest_url_truncated`, logcat ~4KB cut): the last pair of a
//    truncated URL may be cut mid-value, so it is dropped as unreliable. A
//    truncated/intact pair merges only when every key param of the intact URL is
//    present, complete and equal in the truncated one, and the truncated URL has
//    no key param the intact one lacks. Two truncated URLs never merge.

export const SETTLEMENT_PARAM = 'settlement_price';

// Twin of proxy mediationNetworks.js GENERIC_VOLATILE_PARAMS / NETWORKS[].volatileParams.
export const GENERIC_VOLATILE_PARAMS = ['ts', 'timestamp', 'rnd', 'rand', 'cb', 'cachebuster', '_', 'ord'];
export const NETWORK_VOLATILE_PARAMS = {
  liftoff: ['ts', 'timestamp', 'rnd', 'cb', 'cachebuster', '_'],
};

/** volatileParamsFor(networkId) -> sorted lowercase string[] (never has settlement_price). */
export function volatileParamsFor(networkId) {
  const set = new Set(GENERIC_VOLATILE_PARAMS);
  for (const p of NETWORK_VOLATILE_PARAMS[networkId] || []) set.add(String(p).toLowerCase());
  set.delete(SETTLEMENT_PARAM);
  return [...set].sort();
}

export const DUPLICATE_BILLING_CODE = 'duplicate_billing_ping';
export const duplicateBillingTip = (n) => `${n} identical billing pings in this slot; possible double billing`;

const isBill = (r) => !!(r && r.validData && r.validData.kind === 'bill');

function keyOf(r) {
  if (!r) return undefined;
  if (r._k != null) return r._k;
  if (r.requestId != null) return r.requestId;
  return r.id;
}

function tsOf(r) {
  const pick = (v) => {
    if (!v && v !== 0) return 0;
    if (typeof v === 'number') return v;
    const n = Date.parse(v);
    return Number.isNaN(n) ? 0 : n;
  };
  return pick(r && r.startedAt) || pick(r && r.startedAtMs) || pick(r && r.timing && r.timing.start)
    || pick(r && r.startedAtISO) || pick(r && r.timestamp) || 0;
}

function deviceOf(r) {
  const d = r && (r.deviceId != null ? r.deviceId : (r._deviceTab != null ? r._deviceTab : r.device));
  if (d && typeof d === 'object') return String(d.id || d.name || d.ip || '');
  return d == null ? '' : String(d);
}

function isTruncated(r) {
  const has = (list) => Array.isArray(list) && list.some((i) => i && i.code === 'ingest_url_truncated');
  return has(r && r.issues) || has(r && r.validData && r.validData.issues);
}

function decode(s) {
  try { return decodeURIComponent(String(s).replace(/\+/g, ' ')); } catch { return String(s); }
}

/** Split a URL into host, path and ordered [key, value] query pairs. Never throws. */
export function parseBillUrl(url) {
  const s = String(url == null ? '' : url);
  const m = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]*)([^?#]*)(?:\?([^#]*))?/i.exec(s);
  const host = m ? m[1].toLowerCase() : '';
  const path = m ? m[2] : '';
  const query = m && m[3] ? m[3] : '';
  const pairs = query
    ? query.split('&').filter((p) => p !== '').map((p) => {
      const i = p.indexOf('=');
      return i < 0 ? [decode(p).toLowerCase(), ''] : [decode(p.slice(0, i)).toLowerCase(), decode(p.slice(i + 1))];
    })
    : [];
  return { host, path, pairs };
}

/** Descriptor used for comparisons: non-volatile pairs as sorted "k=v" strings. */
function describe(r, networkId) {
  const { host, path, pairs } = parseBillUrl(r && r.url);
  const truncated = isTruncated(r);
  const volatile = new Set(volatileParamsFor(networkId));
  // A truncated URL's last pair may be cut mid-key or mid-value: unreliable.
  const reliable = truncated ? pairs.slice(0, -1) : pairs;
  const keyPairs = reliable
    .filter(([k]) => k === SETTLEMENT_PARAM || !volatile.has(k))
    .map(([k, v]) => `${k}=${v}`)
    .sort();
  return { host, path, truncated, keyPairs };
}

const sameList = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

/** Can descriptors a and b be the same ping? (host/path already equal by bucket) */
function compatible(a, b) {
  // Two cut URLs: neither shows what the other's cut hides.
  if (a.truncated && b.truncated) return false;
  // Intact pair: key params equal. One cut: the cut's reliable key params must
  // equal the intact URL's, i.e. the cut fell after every key param.
  return sameList(a.keyPairs, b.keyPairs);
}

/**
 * Which slot lifecycle each bill row belongs to (via the mediation phase's buyer
 * rowKeys). A row claimed by two lifecycles is ambiguous and omitted.
 */
function attributeBills(lifecycles, rowsByKey) {
  const owner = new Map(); // rowKey -> { lc, buyerId } | null (ambiguous)
  for (const lc of Array.isArray(lifecycles) ? lifecycles : []) {
    const buyers = lc && lc.phases && lc.phases.mediation && lc.phases.mediation.buyers;
    for (const b of Array.isArray(buyers) ? buyers : []) {
      for (const k of Array.isArray(b && b.rowKeys) ? b.rowKeys : []) {
        const row = rowsByKey.get(k);
        if (!isBill(row)) continue;
        if (owner.has(k) && owner.get(k) && owner.get(k).lc !== lc) owner.set(k, null);
        else if (!owner.has(k)) owner.set(k, { lc, buyerId: b.id });
      }
    }
  }
  return owner;
}

/**
 * mergeBillingPings(rows, lifecycles)
 *   -> { groups, byRep, memberOf }
 *  groups  : merged groups with count > 1, each
 *            { key, rep, members, count, lifecycleKey, networkId, issues[] }
 *  byRep   : Map(repRowKey -> group)
 *  memberOf: Map(memberRowKey -> repRowKey) for every NON-first member
 * `rows` need `_k`. The first (earliest) ping is the representative and the slot
 * story's bill evidence. Never throws; a bad input yields no groups.
 */
export function mergeBillingPings(rows, lifecycles) {
  const out = { groups: [], byRep: new Map(), memberOf: new Map() };
  try {
    const list = (Array.isArray(rows) ? rows : []).filter((r) => r && keyOf(r) != null);
    const rowsByKey = new Map(list.map((r) => [keyOf(r), r]));
    const owner = attributeBills(lifecycles, rowsByKey);

    const bills = list
      .filter((r) => isBill(r) && owner.get(keyOf(r)))
      .sort((a, b) => tsOf(a) - tsOf(b));

    const buckets = new Map(); // bucket string -> [{ members, exemplarDesc }]
    for (const r of bills) {
      const { lc, buyerId } = owner.get(keyOf(r));
      const networkId = String((r.validData && r.validData.networkId) || buyerId || '').toLowerCase();
      const desc = describe(r, networkId);
      const bucket = JSON.stringify([deviceOf(r), networkId, lc.key == null ? '' : String(lc.key), desc.host, desc.path]);
      const groups = buckets.get(bucket) || [];
      buckets.set(bucket, groups);
      const hit = groups.find((g) => compatible(g.exemplar, desc));
      if (hit) {
        hit.members.push(r);
        // prefer an intact exemplar so later pings compare against the full URL
        if (hit.exemplar.truncated && !desc.truncated) hit.exemplar = desc;
      } else {
        groups.push({ members: [r], exemplar: desc, lc, networkId });
      }
    }

    for (const groups of buckets.values()) {
      for (const g of groups) {
        if (g.members.length < 2) continue;
        const rep = g.members[0];
        const repKey = keyOf(rep);
        const n = g.members.length;
        const group = {
          key: repKey,
          rep,
          members: g.members,
          count: n,
          lifecycleKey: g.lc.key,
          networkId: g.networkId,
          issues: [{
            code: DUPLICATE_BILLING_CODE, level: 'info', severity: 'info', msg: duplicateBillingTip(n),
          }],
        };
        out.groups.push(group);
        out.byRep.set(repKey, group);
        g.members.slice(1).forEach((m) => out.memberOf.set(keyOf(m), repKey));
      }
    }
  } catch {
    return { groups: [], byRep: new Map(), memberOf: new Map() };
  }
  return out;
}

/**
 * Rows to render: later members of a merged group are folded away unless the
 * group is expanded. `expanded` is a Set of representative row keys. Pure.
 */
export function foldMergedRows(rows, merge, expanded) {
  const list = Array.isArray(rows) ? rows : [];
  if (!merge || !merge.memberOf || merge.memberOf.size === 0) return list.slice();
  const open = expanded instanceof Set ? expanded : new Set();
  const present = new Set(list.map(keyOf));
  const out = [];
  for (const r of list) {
    const k = keyOf(r);
    const rep = merge.memberOf.get(k);
    // A later ping is folded under its representative - but only when that
    // representative is in THIS list (it may sit in another visit group).
    if (rep !== undefined && present.has(rep)) continue;
    out.push(r);
    const g = merge.byRep.get(k);
    if (g && open.has(k)) {
      for (const m of g.members.slice(1)) if (present.has(keyOf(m))) out.push(m);
    }
  }
  return out;
}

/**
 * Duplicate-bill facts for ONE slot lifecycle (feeds buildSlotStory opts).
 * -> [{ buyerId, name, count, rowKey (first ping), rowKeys }] for groups with N>1.
 */
export function billDuplicatesForLifecycle(lc, rowsByKey) {
  try {
    const get = (k) => (rowsByKey && typeof rowsByKey.get === 'function' ? rowsByKey.get(k) : (rowsByKey || {})[k]);
    const rows = [];
    const buyerOf = new Map();
    const buyers = lc && lc.phases && lc.phases.mediation && lc.phases.mediation.buyers;
    for (const b of Array.isArray(buyers) ? buyers : []) {
      for (const k of Array.isArray(b && b.rowKeys) ? b.rowKeys : []) {
        const r = get(k);
        if (r && isBill(r)) {
          rows.push({ ...r, _k: k });
          buyerOf.set(k, b);
        }
      }
    }
    if (!rows.length) return [];
    const merged = mergeBillingPings(rows, [lc]);
    return merged.groups.map((g) => {
      const b = buyerOf.get(g.key);
      return {
        buyerId: b ? b.id : g.networkId,
        name: (b && (b.name || b.id)) || g.networkId,
        count: g.count,
        rowKey: g.key,
        rowKeys: g.members.map(keyOf),
      };
    });
  } catch {
    return [];
  }
}
